// React
import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Libraries
import type { RowSelectionState } from '@tanstack/react-table';

// Shared components
import { AnimatePresence } from 'framer-motion';
import { Toaster } from '@/components/ui/toaster';
import { Page } from '../layouts/Page';
import { EventsSelectionDock } from './sections/EventsSelectionDock';
import { SelectDocumentsModal } from './sections/EventsToolbar/SelectDocumentsModal';
import { PageHeaderTotalLabel, PageHeaderSearchBar } from '../layouts/PageHeader';
import { ResponsivePageHeader } from '../layouts/ResponsivePageHeader';
import { DataTableFooter } from '@components/DataTable/DataTableFooter';

// Local components
import { AllEventsPage } from './pages/AllEvents/page';
import { EventsMobileFooter } from './sections/EventsMobileFooter';
import { OrderDetailSheet } from '../Sales/OrderManagement/Sections/OrderDetailSheet';
import { SalespersonFilterSheet } from '../Sales/OrderManagement/Sections/SalespersonFilterSheet';
import { EventsDateFilterSheet } from './sections/EventsDateFilterSheet';
import { CreateOrderDialog } from '../Sales/OrderManagement/CreateOrder/CreateOrderDialog';
import { openActivityCentrePanel } from '@components/ActivityCentre/useActivityCentrePanel';
import { openMobileSidebar } from '../MobileSidebar/mobileSidebarEvents';

// Hooks
import { useTranslation } from '@hooks/useTranslation';
import { useEvents } from '@hooks/events';
import { searchQueryParam } from '@/react/utils/searchQuery';
import { useGenerateEventOrders } from '@hooks/events';
import { usePostHog } from '@posthog/react';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useEventsRouterSearch } from '@hooks/useEventsRouterSearch';
import { toast } from '@/hooks/use-toast';

// Constants
import { SEARCH_BAR_WIDTH } from '../../constants';

// API
import { buildEventDateParam, getEvents, PUBLISHED_EVENT_STATES, type EventsListParams } from '@api/events';
import { actionable422Message } from '@/react/api/client';
import { can, CAPABILITIES } from '@/react/lib/capabilities';

// Types
import type { EventManagementProps, EventListItem, GenerateBarMode } from './types';
import type { Salesperson, Order } from '@api/orders';

const DEFAULT_SALESPERSONS: Salesperson[] = [];
const EMPTY_EVENTS: EventListItem[] = [];

// "Select all events matching date" resolves to the actual set of event ids for
// the current filters, paging through the API (max page size) so selection is
// real on every page — not a page-1-only approximation. The page cap is a
// runaway guard; a single delivery date never approaches it.
const SELECT_ALL_PER_PAGE = 200;
const SELECT_ALL_MAX_PAGES = 50;

async function fetchAllMatchingEventIds(filters: EventsListParams): Promise<string[]> {
  const ids: string[] = [];
  for (let page = 1; page <= SELECT_ALL_MAX_PAGES; page++) {
    const {events, pagination} = await getEvents({...filters, page, perPage: SELECT_ALL_PER_PAGE});
    for (const event of events) ids.push(String(event.id));
    if (events.length === 0 || page * SELECT_ALL_PER_PAGE >= pagination.total) break;
  }
  return ids;
}

// Parse the URL `date` (YYYY-MM-DD) into a local Date. We use the date-string
// constructor with explicit numeric fields so we don't accidentally land on
// the previous day in negative-UTC timezones.
function parseUrlDate(raw: string): Date | undefined {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return undefined;
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

function formatUrlDate(date: Date | undefined): string {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export default function EventManagement({
  salespersons = DEFAULT_SALESPERSONS,
  currentUser,
}: EventManagementProps) {
  const posthog = usePostHog();
  const {t} = useTranslation();
  const router = useEventsRouterSearch();
  const {search} = router;

  const [searchInput, setSearchInput] = useState(search.q);
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // Re-sync local input when URL changes externally (popstate / in-router nav)
  useEffect(() => {
    setSearchInput(search.q);
  }, [search.q]);

  // Sync debounced typing back to URL (page-1 reset is handled by setQuery)
  useEffect(() => {
    if (debouncedSearch !== search.q) {
      router.setQuery(debouncedSearch);
    }
  }, [debouncedSearch, search.q, router]);

  const selectedDate = useMemo(() => parseUrlDate(search.date), [search.date]);
  const selectedSalespersonIds = useMemo(
    () => search.salespersonIds.map(String),
    [search.salespersonIds],
  );

  // "Published" is a display-level grouping over the raw AASM event states;
  // expand it before it hits the API, which filters on the real states.
  const expandedEventStatuses = useMemo(() => {
    const values = new Set<string>();
    for (const status of search.eventStatuses) {
      if (status === 'published') {
        PUBLISHED_EVENT_STATES.forEach((state) => values.add(state));
      } else {
        values.add(status);
      }
    }
    return Array.from(values);
  }, [search.eventStatuses]);

  const params: EventsListParams = {
    page: search.page,
    perPage: search.perPage,
    eventDate: buildEventDateParam(search.tab, selectedDate),
    salespersonIds: search.salespersonIds.length > 0 ? search.salespersonIds : undefined,
    eventTypes: search.formats.length > 0 ? search.formats : undefined,
    orderStatuses: search.orderStatuses.length > 0 ? search.orderStatuses : undefined,
    eventStatuses: expandedEventStatuses.length > 0 ? expandedEventStatuses : undefined,
    q: searchQueryParam(debouncedSearch),
  };

  const {data, isLoading, isFetching} = useEvents(params);
  const events = data?.events ?? EMPTY_EVENTS;
  const totalCount = data?.pagination.total ?? 0;
  const totalUnfiltered = data?.totalUnfiltered ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / search.perPage));

  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [salespersonSheetOpen, setSalespersonSheetOpen] = useState(false);
  const [dateSheetOpen, setDateSheetOpen] = useState(false);

  // ─── Event selection (GRAIN-1488) ──────────────────────────────────────────
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  // True only while the selection holds *every* matching event (set by
  // "select all matching date", cleared by any manual edit). Drives the
  // action's disabled state and the reset-on-filter-change below.
  const [allMatchingDate, setAllMatchingDate] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  // Snapshot of the manual selection taken the moment "select all matching
  // date" is checked, so unchecking can revert to exactly that base instead of
  // wiping or keeping the whole matching set. Null when not in all-matching
  // mode. A ref (not state) — it only feeds the toggle handler and never needs
  // to trigger a re-render.
  const baseSelectionRef = useRef<RowSelectionState | null>(null);
  // Counts selection writes, so the async select-all below can tell whether
  // anything overwrote the selection while it was resolving.
  const selectionEpochRef = useRef(0);

  // The only writer — the bump lives here so no call site can forget it.
  const commitSelection = useCallback(
    (next: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)) => {
      selectionEpochRef.current += 1;
      setRowSelection(next);
    },
    [],
  );

  // A single date is in effect via the picker or the Today/Tomorrow tabs —
  // gates the "select all events matching date" action.
  const hasDateFilter = buildEventDateParam(search.tab, selectedDate) !== undefined;

  // Selection always holds real event ids, so the count is just their tally —
  // accurate across pages whether selected manually or via all-matching. Tally
  // truthy values only: a `{id: false}` entry (left behind by an unchecked row)
  // is not a selection and must not inflate the dock's count.
  const selectedCount = Object.values(rowSelection).filter(Boolean).length;

  // The dock checkbox reflects whether *every* matching event is selected, so it
  // also ticks when the user builds the full set by hand — not only via the
  // action. Scoped to a single page (all matching events loaded, so
  // events.length === totalCount) so a partial multi-page manual selection can't
  // spuriously check it; testing the matching rows directly — rather than
  // comparing counts — keeps it immune to selections carried over from another
  // filter (those ids aren't among `events`).
  const allMatchingSelected =
    hasDateFilter &&
    totalCount > 0 &&
    events.length === totalCount &&
    events.every((event) => rowSelection[event.id]);

  // What the checkbox shows: checked via the action (mode flag) or because the
  // manual selection already covers everything.
  const selectAllChecked = allMatchingDate || allMatchingSelected;

  // A manual checkbox edit means the selection is no longer "everything for the
  // date", so drop the all-matching flag. The base snapshot is intentionally
  // left untouched: it is only ever read in the uncheck branch below, which is
  // reachable solely after a fresh check re-captures it, so it can never be
  // read while stale — no defensive clearing needed here.
  const handleRowSelectionChange = useCallback((selection: RowSelectionState) => {
    commitSelection(selection);
    setAllMatchingDate(false);
  }, [commitSelection]);

  // Toggle "select all events matching date". Checking (re)captures the current
  // manual selection as the base, then resolves the full id set from the API
  // (not just the loaded page) so every page's checkboxes are genuinely
  // selected and de-selecting one can't collapse the whole set.
  const handleSelectAllMatchingDate = useCallback(async () => {
    // Unchecked via the action: revert to the pre-select-all base — deselecting
    // the rows select-all added while keeping any rows the user had manually
    // ticked beforehand — then clear the flag and the now-consumed snapshot.
    // Only this branch reads the snapshot, and it's only reachable right after a
    // fresh check captured it, so the snapshot can't be stale.
    if (allMatchingDate) {
      commitSelection(baseSelectionRef.current ?? {});
      setAllMatchingDate(false);
      baseSelectionRef.current = null;
      return;
    }
    // Checked only because the manual selection already covers every matching
    // event (no action snapshot exists) → unchecking just drops the matching
    // rows, leaving any selection carried over from another filter intact.
    if (allMatchingSelected) {
      commitSelection((prev) => {
        const next = {...prev};
        for (const event of events) delete next[event.id];
        return next;
      });
      return;
    }
    // Capture the base before the await so a re-render mid-fetch can't overwrite
    // it (rowSelection is in the dep list, keeping this closure current).
    baseSelectionRef.current = rowSelection;
    // Paging the full set takes several round-trips; anything that writes the
    // selection meanwhile is newer than these ids.
    const requestedEpoch = selectionEpochRef.current;
    setSelectingAll(true);
    try {
      const ids = await fetchAllMatchingEventIds({
        eventDate: params.eventDate,
        salespersonIds: params.salespersonIds,
        q: params.q,
      });
      // Lost the race — these ids belong to a selection that no longer exists.
      if (selectionEpochRef.current !== requestedEpoch) return;
      commitSelection(Object.fromEntries(ids.map((id) => [id, true])));
      setAllMatchingDate(true);
    } finally {
      setSelectingAll(false);
    }
  }, [allMatchingDate, allMatchingSelected, events, rowSelection, params.eventDate, params.salespersonIds, params.q]);

  const handleSalespersonChange = useCallback(
    (ids: string[]) => {
      router.setSalespersonIds(ids.map(Number).filter((n) => Number.isFinite(n) && n > 0));
    },
    [router],
  );

  const handleDateChange = useCallback(
    (date: Date | undefined) => {
      router.setDate(formatUrlDate(date));
    },
    [router],
  );

  const handleNewOrder = useCallback(() => {
    setCreateDialogOpen(true);
  }, []);

  // Mirrors the Orders page: toast a link to the new order that opens the
  // existing OrderDetailSheet. The events list itself doesn't change — a fresh
  // order has no fulfilment events yet.
  const handleOrderCreated = useCallback((order: Order) => {
    toast({
      description: (
        <span>
          New order:{' '}
          <button
            type="button"
            onClick={() => setSelectedOrderId(order.id)}
            className={UNDERLINE_STATIC}
            aria-label={`View details for order ${order.reference_number}`}
          >
            {order.reference_number}
          </button>
        </span>
      ),
    });
  }, []);

  // ─── Generate documents mutation ───────────────────────────────────────────
  // Keyed off rowSelection (accurate across pages — manual or "select all
  // matching date"), not `events`, which only holds the current page.
  const selectedEventIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  );

  // Drafts are skipped server-side, so the button needs a published event.
  const selectedPageEvents = useMemo(
    () => events.filter((event) => rowSelection[event.id]),
    [events, rowSelection],
  );
  const selectedEventDescriptors = useMemo(
    () => selectedPageEvents.map((event) => ({eventType: event.eventTypeKey, productLine: event.productLine})),
    [selectedPageEvents],
  );
  const selectedHasPublished = selectedPageEvents.some((event) => PUBLISHED_EVENT_STATES.includes(event.eventStatus));
  const canGenerate = can(currentUser, CAPABILITIES.EVENT_ORDERS_GENERATE);
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);

  const generateMutation = useGenerateEventOrders();
  // `reset` is stable, the result object isn't — depending on the object would
  // re-run the effect below on every render.
  const {reset: resetGenerateMutation} = generateMutation;

  // An effective filter change retires the selection and any lingering generate
  // status: both are scoped to the events the previous filter showed. Pagination
  // is exempt — selection is id-keyed, so `page` stays out of the key. Uses
  // `params.q` rather than the raw input, since a term too short to search
  // leaves the list unchanged. Mirrors the reset effect in ProductsTable.tsx.
  const salespersonKey = selectedSalespersonIds.join(',');
  const filterKey = `${search.tab}|${search.date}|${salespersonKey}|${search.formats.join(',')}|${search.orderStatuses.join(',')}|${search.eventStatuses.join(',')}|${params.q ?? ''}`;
  const filterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (filterKeyRef.current === filterKey) return; // unchanged, or first render
    filterKeyRef.current = filterKey;
    commitSelection({});
    setAllMatchingDate(false);
    baseSelectionRef.current = null;
    resetGenerateMutation();
  }, [filterKey, resetGenerateMutation]);

  const generateBarMode = useMemo<GenerateBarMode | undefined>(() => {
    if (generateMutation.isPending) return 'loading';
    if (generateMutation.isSuccess) return 'queued';
    // An actionable 422 is already toasted by the mutation hook; showing the
    // generic "please try again" pill alongside it would contradict it.
    if (generateMutation.isError) {
      return actionable422Message(generateMutation.error) ? undefined : 'error';
    }
    return undefined;
  }, [
    generateMutation.isPending,
    generateMutation.isSuccess,
    generateMutation.isError,
    generateMutation.error,
  ]);

  const handleGenerateDocuments = useCallback(
    (documentTypes: string[]) => {
      if (selectedEventIds.length === 0) return;
      // Date is optional: off the date tabs it's undefined and the backend
      // derives the serving date(s) from the selected events.
      const date = buildEventDateParam(search.tab, selectedDate);
      generateMutation.mutate({
        date,
        document_types: documentTypes,
        all_events_on_date: false,
        event_ids: selectedEventIds.map(Number),
      });
      posthog.capture('event_orders_generated', {
        event_count: selectedEventIds.length,
        document_types: documentTypes,
      });
    },
    [generateMutation, search.tab, selectedDate, selectedEventIds, posthog],
  );

  // Row-level generate: one specific event, document types already resolved by
  // the row menu (wedding variant picked per-event). Reuses the same mutation so
  // the selection dock surfaces the loading/queued/error bar. Date is omitted so
  // the backend derives the serving date(s) from the event itself, regardless of
  // the current date filter.
  const handleGenerateForEvent = useCallback(
    (eventId: number, documentTypes: string[]) => {
      if (documentTypes.length === 0) return;
      generateMutation.mutate({
        document_types: documentTypes,
        all_events_on_date: false,
        event_ids: [eventId],
      });
    },
    [generateMutation],
  );

  const handleDismissGenerateBar = useCallback(() => {
    resetGenerateMutation();
    commitSelection({});
  }, [commitSelection, resetGenerateMutation]);

  const openProcessingPanel = useCallback(() => openActivityCentrePanel('processing'), []);

  return (
    <>
      <Toaster />
      <Page className="relative">
        <Page.Header className="py-6 md:py-0">
          <ResponsivePageHeader
            mobile={{
              title: t('title'),
              count: isLoading ? '…' : totalCount.toLocaleString(),
              searchValue: searchInput,
              onSearchChange: setSearchInput,
              searchPlaceholder: t('header.search_placeholder'),
              searchAriaLabel: t('header.search_label'),
              openNavigationAriaLabel: t('header.open_navigation'),
              openSearchAriaLabel: t('header.open_search'),
              closeSearchAriaLabel: t('header.close_search'),
              onOpenNavigation: openMobileSidebar,
            }}
          >
            <div className="shrink-0">
              <PageHeaderTotalLabel
                count={totalUnfiltered}
                label={t('header.total_unfiltered')}
              />
            </div>
            <div
              className="flex-1 min-w-0 flex justify-center"
              style={{flexBasis: SEARCH_BAR_WIDTH}}
            >
              <PageHeaderSearchBar
                value={searchInput}
                onChange={setSearchInput}
                placeholder={t('header.search_placeholder')}
                ariaLabel={t('header.search_label')}
                shortcutKey="f"
                shortcutModifier="alt"
                shortcutAriaLabel={t('header.search_shortcut_aria')}
              />
            </div>
            <div
              className="min-w-0 invisible"
              aria-hidden="true"
              style={{flexShrink: 99}}
            >
              <PageHeaderTotalLabel
                count={totalUnfiltered}
                label={t('header.total_unfiltered')}
              />
            </div>
          </ResponsivePageHeader>
        </Page.Header>
        <Page.Body>
          <AllEventsPage
            currentTab={search.tab}
            totalCount={totalCount}
            events={events}
            salespersons={salespersons}
            selectedSalespersonIds={selectedSalespersonIds}
            onTabChange={router.setTab}
            onSalespersonChange={handleSalespersonChange}
            selectedFormats={search.formats}
            onFormatsChange={router.setFormats}
            selectedOrderStatuses={search.orderStatuses}
            onOrderStatusesChange={router.setOrderStatuses}
            selectedEventStatuses={search.eventStatuses}
            onEventStatusesChange={router.setEventStatuses}
            onNewOrder={handleNewOrder}
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            onOpenDateFilter={() => setDateSheetOpen(true)}
            isLoading={isLoading}
            isFetching={isFetching}
            onEventIdClick={setSelectedOrderId}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
            onGenerateEvent={handleGenerateForEvent}
            currentUser={currentUser}
            pagination={{
              page: Math.min(search.page, totalPages),
              totalPages,
              onPageChange: router.setPage,
            }}
          />
        </Page.Body>
        <Page.Footer>
          <EventsMobileFooter
            className="md:hidden"
            onOpenSalespersonFilter={() => setSalespersonSheetOpen(true)}
          />
          <DataTableFooter
            currentPage={Math.min(search.page, totalPages)}
            totalPages={totalPages}
            totalCount={totalCount}
            perPage={search.perPage}
            onPageChange={router.setPage}
            onPerPageChange={router.setPerPage}
            className="hidden md:flex flex-col justify-center h-full px-6 sm:px-8 lg:px-16"
          />
        </Page.Footer>

        {/* Floating selection dock — rendered at the Page level (a sibling of
            Body/Footer, outside Body's overflow) so it can straddle the footer's
            top border, vertically centred on it, per the Figma (GRAIN-1488).
            `bottom-23` matches the footer height (Page.Footer is `h-23`) to land
            the wrapper's bottom edge on the footer's top border; `translate-y-1/2`
            then centres the pill on that line. Centred horizontally over the
            content column (`inset-x-0`). */}
        <div className="pointer-events-none absolute inset-x-0 bottom-23 z-20 flex translate-y-1/2 justify-center">
          <AnimatePresence>
            {(selectedCount >= 1 || generateBarMode) && (
              <EventsSelectionDock
                key="events-selection-dock"
                selectedCount={selectedCount}
                canSelectAllMatchingDate={hasDateFilter}
                allMatchingDate={selectAllChecked}
                selectingAllMatchingDate={selectingAll}
                onSelectAllMatchingDate={handleSelectAllMatchingDate}
                onGenerateDocuments={canGenerate ? () => setDocumentsModalOpen(true) : undefined}
                generateDocumentsDisabled={!selectedHasPublished}
                mode={generateBarMode ?? 'selected'}
                // Keep the selection after a failure so the rep can retry.
                onGenerateDismiss={generateBarMode === 'error' ? resetGenerateMutation : handleDismissGenerateBar}
                onGenerateAutoDismiss={handleDismissGenerateBar}
                onQueuedLinkClick={openProcessingPanel}
              />
            )}
          </AnimatePresence>
        </div>
      </Page>

      <SelectDocumentsModal
        open={documentsModalOpen}
        onOpenChange={setDocumentsModalOpen}
        selectedEvents={selectedEventDescriptors}
        onGenerate={(documentTypes) => {
          setDocumentsModalOpen(false);
          handleGenerateDocuments(documentTypes);
        }}
      />

      <SalespersonFilterSheet
        open={salespersonSheetOpen}
        onOpenChange={setSalespersonSheetOpen}
        salespersons={salespersons}
        selectedIds={selectedSalespersonIds}
        onSelectionChange={handleSalespersonChange}
        title={t('toolbar.salesperson')}
      />

      <EventsDateFilterSheet
        open={dateSheetOpen}
        onOpenChange={setDateSheetOpen}
        selected={selectedDate}
        onSelect={handleDateChange}
      />

      <OrderDetailSheet
        orderId={selectedOrderId}
        open={selectedOrderId !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedOrderId(null);
        }}
        currentUser={currentUser}
      />

      <CreateOrderDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSuccess={handleOrderCreated}
        salespersons={salespersons}
        currentUser={currentUser}
      />
    </>
  );
}
